import { useState, useEffect, useCallback, useMemo } from "react";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { COLLECTIONS, ROOT_COLLECTIONS } from "../../../../services/collections";

import { getDateRange } from "../utils/attendanceUtils";

/**
 * useProfileAttendance
 * 
 * Hook que busca os attendanceLogs diretamente da subcoleção correta:
 *   tenants/{idTenant}/members/{idMember}/attendanceLogs
 * 
 * O campo `date` no log é uma string "YYYY-MM-DD".
 */
const useProfileAttendance = (idTenant, idMember) => {
    const [allLogs, setAllLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState("WEEK");
    const [customRange, setCustomRange] = useState({ startDate: null, endDate: null });

    /**
     * Busca TODOS os attendanceLogs do membro uma única vez.
     */
    const fetchLogs = useCallback(async () => {
        if (!idTenant || !idMember) return;

        setLoading(true);
        setError(null);

        try {
            const logsRef = collection(
                db,
                ROOT_COLLECTIONS.TENANTS,
                idTenant,
                COLLECTIONS.MEMBERS,
                idMember,
                COLLECTIONS.ATTENDANCE_LOGS
            );

            const q = query(logsRef, orderBy("date", "desc"));
            const snapshot = await getDocs(q);

            const logs = snapshot.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));

            setAllLogs(logs);
        } catch (err) {
            console.error("[useProfileAttendance] Erro ao buscar logs:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [idTenant, idMember]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    /**
     * Range ativo (computado).
     */
    const activeRange = useMemo(() => {
        if (filter === "CUSTOM") {
            return {
                startDate: customRange.startDate,
                endDate: customRange.endDate
            };
        }
        return getDateRange(filter);
    }, [filter, customRange]);

    /**
     * Logs filtrados pelo período selecionado (client-side).
     */
    const filteredLogs = useMemo(() => {
        if (!allLogs || allLogs.length === 0) return [];

        const { startDate, endDate } = activeRange;

        // Se não tiver range (ALL), retorna tudo
        if (!startDate && !endDate) return allLogs;

        return allLogs.filter(log => {
            if (!log.date) return false;
            if (startDate && log.date < startDate) return false;
            if (endDate && log.date > endDate) return false;
            return true;
        });
    }, [allLogs, activeRange]);

    const handleFilterChange = useCallback((newFilter) => {
        setFilter(newFilter);
    }, []);

    const handleCustomRange = useCallback(({ startDate, endDate }) => {
        setFilter("CUSTOM");
        setCustomRange({
            startDate: startDate || null,
            endDate: endDate || null
        });
    }, []);

    return {
        allLogs,
        filteredLogs,
        loading,
        error,
        filter,
        activeRange,
        customRange,
        handleFilterChange,
        handleCustomRange,
        refresh: fetchLogs
    };
};

export default useProfileAttendance;
